"use client";

import React, { useEffect, useMemo } from "react";
import { CheckCircle, CircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChartPieIcon } from "@/components/ui/icons";
import { EngSpecFileDiff } from "./EngSpec";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { usePrevious } from "@/utils/utils";
import { useSelector } from "react-redux";
import useActivityStore from "@/store/zustand/activityStore";

interface FileChangeProps {
  file: {
    id?: string;
    summary?: string;
    hasChange?: boolean;
    termCommand?: string;
    storyboardName?: string;
    path?: string;
    executedActivityId?: string;
  };

  index?: number;

  minimal?: boolean;
  readOnly?: boolean;
}

export const FileChange = React.memo(
  ({ index, file, minimal, readOnly }: FileChangeProps) => {
    const engSpecOpenedFileId = useAiContextStore(
      (state) => state.engSpecOpenedFileId,
    );

    const setEngSpecOpenedFile = useAiContextStore(
      (state) => state.setEngSpecOpenedFile,
    );

    const prevChange = usePrevious(file?.hasChange);
    useEffect(() => {
      if (!prevChange && file?.hasChange) {
        // @ts-expect-error TS2345
        setEngSpecOpenedFile(file.id, Boolean(file?.termCommand));
      }
    }, [file?.hasChange]);

    const activityStreamList = useActivityStore(
      (state) => state.activityStreamList,
    );

    const hasSavedActivity = useMemo(() => {
      return (
        file?.executedActivityId === "RAN_COMMAND" ||
        activityStreamList?.find?.(
          (activity) =>
            file?.executedActivityId === activity.id && !activity.change_undone,
        )
      );
    }, [activityStreamList, file?.executedActivityId]);
    return (
      <div
        className={cn(
          "flex flex-col bg-background-primary px-4 p-2 border-b hover:bg-background-secondary cursor-pointer transition w-full",
          index === 0 && "border-t",
          engSpecOpenedFileId === file.id && "bg-background-secondary",
          readOnly && "cursor-default hover:bg-background-primary",
          minimal && "bg-transparent hover:bg-transparent",
        )}
        onClick={() => {
          if (readOnly) return;

          // @ts-expect-error TS2345
          setEngSpecOpenedFile(file.id, Boolean(file?.termCommand));
        }}
      >
        <div
          className={cn(
            "flex flex-row gap-3 items-center w-full justify-between",
            minimal && "flex-col gap-2 items-start",
          )}
        >
          <div className="flex flex-row gap-1.5 items-center">
            {minimal ? null : hasSavedActivity ? (
              <CheckCircle className="size-4 shrink-0 stroke-green-500" />
            ) : file?.hasChange ? (
              <ChartPieIcon className="size-4 shrink-0 stroke-[#FFCC00]" />
            ) : (
              <CircleIcon className="size-4 shrink-0" />
            )}
            <div className="bg-background-tertiary text-text-secondary rounded-sm text-2xs p-1 px-2 flex items-center justify-center font-medium">
              {file?.termCommand ? "CMD" : "FILE"}
            </div>
            <div
              className={cn(
                "text-text-primary text-xs font-medium",
                !file?.summary && "text-text-secondary",
              )}
            >
              {file?.summary || "Summary"}
            </div>
          </div>

          <div
            className={cn(
              "border border-secondary-n-700 rounded-md text-2xs font-mono py-0.5 px-2 bg-secondary-n-950 text-secondary-n-400 break-all",
              {
                hidden:
                  !file?.path && !file?.termCommand && !file?.storyboardName,
                "text-white!": engSpecOpenedFileId === file.id,
              },
            )}
          >
            {file?.path ||
              (file?.termCommand
                ? "Terminal"
                : file?.storyboardName
                  ? "Create Storyboard"
                  : "")}
          </div>
        </div>
      </div>
    );
  },
);
